
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Article } from '@/components/reader-home/types';
import { User } from '@supabase/supabase-js';

export const useArticleService = (
  setArticles: React.Dispatch<React.SetStateAction<Article[]>>
) => {
  // Fetch articles from the database
  const loadArticles = useCallback(async (user: User | null) => {
    try {
      // Get published articles (without trying to join with profiles)
      const { data: articlesData, error: articlesError } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(20);
      
      if (articlesError) throw articlesError;

      // Create a map to store author profiles
      const authorProfiles = new Map();
      
      // Get unique author IDs
      const authorIds = [...new Set((articlesData || []).map(article => article.author_id))];
      
      // Fetch author profiles in a separate query
      if (authorIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', authorIds);
          
        if (profilesError) throw profilesError;
        
        // Create a map of author ID to profile data
        (profilesData || []).forEach(profile => {
          authorProfiles.set(profile.id, profile);
        });
      }

      // Fetch article tags
      const articlesWithTags = await Promise.all((articlesData || []).map(async (article) => {
        const { data: tagData } = await supabase
          .from('article_tags')
          .select(`
            tags(name)
          `)
          .eq('article_id', article.id);
          
        const topics = (tagData || []).map(tag => tag.tags?.name).filter(Boolean);
        
        // Fetch user's read progress for this article if user is logged in
        let progress = 0;
        if (user) {
          const { data: readData, error: readError } = await supabase
            .from('article_reads')
            .select('read_percentage')
            .eq('article_id', article.id)
            .eq('user_id', user.id);
            
          if (!readError && readData && readData.length > 0) {
            progress = readData[0].read_percentage;
          }
        }
        
        // Get the author profile from our map
        const authorProfile = authorProfiles.get(article.author_id);
        
        return {
          ...article,
          authorProfile,
          topics,
          progress
        };
      }));
      
      // Transform the data to match our component expectations
      const formattedArticles: Article[] = articlesWithTags.map(article => {
        // Extract excerpt from content if not provided
        let excerpt = article.excerpt;
        if (!excerpt && article.content?.blocks) {
          const blocks = article.content.blocks;
          const textBlocks = blocks.filter(block => block.type === 'paragraph');
          if (textBlocks.length > 0 && textBlocks[0].data && textBlocks[0].data.text) {
            excerpt = textBlocks[0].data.text.substring(0, 120) + '...';
          }
        }
        
        return {
          id: article.id,
          title: article.title,
          author: article.authorProfile?.display_name || 'Unknown Author',
          author_avatar: article.authorProfile?.avatar_url || 'https://ui-avatars.com/api/?name=' + 
            encodeURIComponent(article.authorProfile?.display_name || 'Unknown Author') + '&background=random',
          authorId: article.author_id,
          date: new Date(article.published_at || article.created_at).toLocaleDateString(),
          image: article.featured_image_url || article.header_image_url || 'https://placehold.co/600x400?text=Article',
          excerpt: excerpt || '', // Don't add fallback text, just leave it empty if not available
          readTime: article.reading_time ? `${article.reading_time}` : '3',
          topics: article.topics || ['General'],
          hasAudio: false, // We could implement this later
          progress: article.progress || 0,
          lead: article.lead || '',
          featured: article.featured || false
        };
      });
      
      setArticles(formattedArticles);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setArticles([]);
    }
  }, [setArticles]);

  return { loadArticles };
};
